import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import { ALL_EQUIPMENTS } from '../../../shared/types/oeeRow.ts';
import { ShiftSlot } from '../../../shared/types/shiftTypes.ts';
import { mapBy, mapToArrayOfKeyVal } from '../../../shared/utils/arrayUtils.ts';
import { dateWithoutTime } from '../../../shared/utils/dateUtils.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { LiveGraphSettings } from '../../components/live-graph-settings/liveGraphSettings.ts';
import { useResizeObserver } from '../../hooks/useResizeObserver.ts';
import { OeeRow } from '../../types/sharedTypeImpl.ts';
import { getShiftColorLight } from '../../utils/liveGraphColors.ts';
import { getGraphValue, OeeGraphValueType } from '../../utils/ooeGraphValueType.ts';

interface DayEntry {
    label: string;
    shifts: ShiftEntry[];
}

interface ShiftEntry {
    shift: ShiftSlot;
    value: number;
    label: string;
}


interface Props {
    className?: string;
    shiftTable: OeeRow[];
    graphValueType: OeeGraphValueType;
    displayOptions: LiveGraphSettings;
}

export function FactoryGraph({
    className,
    shiftTable,
    graphValueType,
    displayOptions,
}: Props) {

    const containerRef = useRef<SVGSVGElement>(null);
    const dimensions = useResizeObserver(containerRef);


    useEffect(() => {
        const data = prepareData(shiftTable, displayOptions);
        drawGraph(containerRef.current!, data, displayOptions);
    }, [shiftTable, graphValueType, dimensions, displayOptions]);


    return (
        <svg
            ref={containerRef}
            className={`${className} live-graphs__graph`}
        />
    );
}


function drawGraph(container: SVGSVGElement, data: DayEntry[], displayOptions: LiveGraphSettings) {
    const root = d3.select(container);
    root.html('');
    const rootRect = container.getBoundingClientRect();

    const margins = { top: 20, right: 10, bottom: 20, left: 10 };
    const width = rootRect.width - margins.left - margins.right;
    const height = rootRect.height - margins.top - margins.bottom;
    const graph = root.append('g')
        .attr('transform', `translate(${margins.left}, ${margins.top})`);

    const xScale = d3.scaleBand()
        .domain(data.map(d => d.label))
        .range([0, width])
        .padding(0.07);
    const yScale = d3.scaleLinear()
        .domain([0, 1])
        .range([height, 0]);

    // Append x-axis
    graph.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(xScale));

    const barWidth = xScale.bandwidth() / displayOptions.visibleShifts.length;
    const textSize = Math.min(barWidth / 2, 18);
    data.forEach((day, dayIndex) => {
        day.shifts.forEach((shift, barIndex) => {
            graph.append('rect')
                .attr('x', xScale(day.label)! + barWidth * barIndex)
                .attr('y', yScale(shift.value))
                .attr('height', height - yScale(shift.value))
                .attr('width', barWidth)
                .attr('fill', getShiftColorLight(shift.shift));

            graph.append('text')
                .attr('x', xScale(day.label)! + barWidth * barIndex + barWidth / 2)
                .attr('y', yScale(shift.value) - 5)
                .attr('text-anchor', 'middle')
                .attr('font-size', `${textSize}px`)
                .text(shift.label);
        });
    });
}


function prepareData(shiftTable: OeeRow[], displayOptions: LiveGraphSettings): DayEntry[] {
    const allEquipmentsRows = shiftTable
        .filter(it => it.equipment === ALL_EQUIPMENTS)
        .filter(it => displayOptions.visibleShifts.includes(it.shiftSlot));
    const mapByDay = mapBy(allEquipmentsRows, it => dateWithoutTime(it.start).getTime());
    const days = mapToArrayOfKeyVal(mapByDay, 'dateMillis', 'shifts') as { dateMillis: number, shifts: OeeRow[] }[];

    sortBy(days, it => it.dateMillis);
    const entries: DayEntry[] = days.map(it => ({
        label: dateToLabel(new Date(it.dateMillis)),
        shifts: it.shifts.sort((a, b) => +a.start - +b.start).map(it => convertShift(it, displayOptions))
    }));
    return entries;
}

function dateToLabel(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: '2-digit' };
    const formatted = new Intl.DateTimeFormat('en-US', options).format(date);
    return formatted;
}

function convertShift(oeeRow: OeeRow, displayOptions: LiveGraphSettings): ShiftEntry {
    const { value } = getGraphValue(oeeRow, displayOptions.valueType, displayOptions.oeeComponents);
    return {
        shift: oeeRow.shiftSlot,
        value,
        label: value ? value.toFixed(2) : '0',
    };
}
